
import type { Vfs, StatInfo, OpenDir, OpenFile, DirEntry } from '../types/vfs';
import type { Dir, promises, Mode } from 'fs';
import { exceptionCode, isFileOrDirectoryMissingError } from '../util/errnoException';
import type { fs as limitedFsNamespace, LimitedFs } from '../nodejs/limitedNodeApi/fs';

type FspType = typeof promises;

interface PathType {
  dirname(path: string): string;
}


class LocalOpenFile implements OpenFile {
  constructor(private handle: promises.FileHandle) {
  }
  async read(buf: Uint8Array, bufOffset: number, count: number, fileOffset: number): Promise<number> {
    const result = await this.handle.read(buf, bufOffset, count, fileOffset);
    return result.bytesRead;
  }
  async write(buf: Uint8Array, bufOffset: number, count: number, fileOffset: number): Promise<number> {
    const result = await this.handle.write(buf, bufOffset, count, fileOffset);
    return result.bytesWritten;
  }
  close(): Promise<void> {
    return this.handle.close();
  }
}

class LocalOpenDir implements OpenDir {
  constructor(private dir: Dir) {
  }
  async read(): Promise<DirEntry[] | null> {
    const entry = await this.dir.read();
    if (entry == null) {
      return null;
    }

    return [{ name: entry.name, isDirectory: entry.isDirectory(), isFile: entry.isFile() }];
  }
  async close(): Promise<void> {
    await this.dir.close();
  }
}

class ReadDir implements OpenDir {
  constructor(private entries: DirEntry[] | null) {
  }
  read(): Promise<DirEntry[] | null> {
    const result = Promise.resolve(this.entries);
    this.entries = null;
    return result;
  }
  close(): Promise<void> {
    return Promise.resolve();
  }
}

// TODO: Why is additionalModeBits not used?
async function ensureSensibleAccessPermissions(fsp: FspType, pt: PathType, path: string, additionalModeBits: number) {
  try {
    const stats = await fsp.stat(path);
    // eslint-disable-next-line no-bitwise
    const newMode = stats.mode | 0b110110110;
    if (newMode != stats.mode) {
      await fsp.chmod(path, newMode);
    }

    const dirStats = await fsp.stat(pt.dirname(path));
    if (stats.uid != dirStats.uid || stats.gid != dirStats.gid) {
      await fsp.chown(path, dirStats.uid, dirStats.gid);
    }
  } catch (ex) {
    console.warn('Could not ensure sensible mode, uid and gid for', path);
  }
}

export class LocalVfs implements Vfs {
  constructor(private fsp: FspType, private pt: PathType) {
  }
  async open(path: string, flags: string): Promise<OpenFile> {
    const fh = await this.fsp.open(path, flags);
    if (flags == 'w') {
      const fileRWForEveryone = 0b110110110;
      await ensureSensibleAccessPermissions(this.fsp, this.pt, path, fileRWForEveryone);
    }
    return new LocalOpenFile(fh);
  }
  async stat(path: string): Promise<StatInfo> {
    const stats = await this.fsp.stat(path);
    return {
      size: stats.size,
      isDirectory: stats.isDirectory(),
      isFile: stats.isFile(),
      modTimeMs: stats.mtimeMs,
      creationTimeMs: stats.birthtimeMs,
    };
  }
  async openDir(path: string): Promise<OpenDir> {
    if (!this.fsp.opendir) {
      const entries = await this.fsp.readdir(path, { withFileTypes: true });
      return new ReadDir(entries.map(e => ({ name: e.name, isDirectory: e.isDirectory(), isFile: e.isFile() })));
    }
    const dir = await this.fsp.opendir(path);
    return new LocalOpenDir(dir);
  }
  rename(oldPath: string, newPath: string): Promise<void> {
    return this.fsp.rename(oldPath, newPath);
  }

  async mkdir(dirPath: string): Promise<void> {
    try {
      await this.fsp.mkdir(dirPath);
    } catch (ex) {
      if (exceptionCode(ex) !== 'EEXIST') {
        throw ex;
      }
      return;
    }
    const fullDirectoryAccessForEveryone = 0b111111111;
    await ensureSensibleAccessPermissions(this.fsp, this.pt, dirPath, fullDirectoryAccessForEveryone);
  }

  async unlink(path: string): Promise<boolean> {
    try {
      await this.fsp.unlink(path);
      return true;
    } catch (ex) {
      if (!isFileOrDirectoryMissingError(ex)) {
        throw ex;
      }
      return false;
    }
  }
  async rmdir(dirPath: string): Promise<void> {
    try {
      await this.fsp.rmdir(dirPath);
    } catch (ex) {
      if (!isFileOrDirectoryMissingError(ex)) {
        throw ex;
      }
    }
  }
}

export type FsType = LimitedFs;

export class LocalCallbackBasedOpenFile implements OpenFile {
  constructor(private fs: FsType, private fd: number) { }

  read(buf: Uint8Array, bufOffset: number, count: number, fileOffset: number): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      this.fs.read(this.fd, buf, bufOffset, count, fileOffset, (err: NodeJS.ErrnoException | null, bytesRead: number) => {
        if (err !== null) {
          reject(err);
        } else {
          resolve(bytesRead);
        }
      });
    })
  }

  write(buf: Uint8Array, bufOffset: number, count: number, fileOffset: number): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      this.fs.write(this.fd, buf, bufOffset, count, fileOffset, (err: NodeJS.ErrnoException | null, bytesWritten: number) => {
        if (err !== null) {
          reject(err);
        } else {
          resolve(bytesWritten);
        }
      });
    });

  }

  async close(): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      this.fs.close(this.fd, (err: NodeJS.ErrnoException | null) => {
        if (err !== null) {
          reject(err);
        } else {
          resolve();
        }
      });
    });
  }
}

class LocalCallbackBasedOpenDir implements OpenDir {
  constructor(private dir: limitedFsNamespace.Dir) {
  }
  async read(): Promise<DirEntry[] | null> {
    const entry = await new Promise<limitedFsNamespace.Dirent | null>((resolve, reject) => {
      this.dir.read((err: NodeJS.ErrnoException | null, dirent: limitedFsNamespace.Dirent | null) => {
        if (err !== null) {
          reject(err);
        } else {
          resolve(dirent);
        }
      });
    });

    if (entry == null) {
      return null;
    }

    return [{ name: entry.name, isDirectory: entry.isDirectory(), isFile: entry.isFile() }];
  }
  async close(): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      this.dir.close((err: NodeJS.ErrnoException | null) => {
        if (err !== null) {
          reject(err);
        } else {
          resolve();
        }
      });
    });
  }
}


export class LocalCallbackBasedVfs implements Vfs {
  constructor(private fs: FsType, private pt: PathType) { }

  async open(path: string, flags: string): Promise<OpenFile> {
    let fd = await new Promise<number>((resolve, reject) => {
      this.fs.open(path, flags, undefined, (err: NodeJS.ErrnoException | null, fd: number) => {
        if (err !== null) {
          reject(err);
        } else {
          resolve(fd);
        }
      });
    });

    if (flags == 'w') {
      const fileRWForEveryone = 0b110110110;
      await ensureSensibleAccessPermissionsCallbackBased(this.fs, this.pt, path, fileRWForEveryone);
    }
    return new LocalCallbackBasedOpenFile(this.fs, fd);
  }

  async stat(path: string): Promise<StatInfo> {
    const stats = await promisifiedStat(this.fs, path);
    return {
      size: stats.size,
      isDirectory: stats.isDirectory(),
      isFile: stats.isFile(),
      modTimeMs: stats.mtimeMs,
      creationTimeMs: stats.birthtimeMs,
    };
  }
  async openDir(path: string): Promise<OpenDir> {
    const dir = await new Promise<limitedFsNamespace.Dir>((resolve, reject) => {
      this.fs.opendir(path, (err: NodeJS.ErrnoException | null, dir: limitedFsNamespace.Dir) => {
        if (err !== null) {
          reject(err);
        } else {
          resolve(dir);
        }
      });
    });
    return new LocalCallbackBasedOpenDir(dir);
  }
  async rename(oldPath: string, newPath: string): Promise<void> {
    return await new Promise<void>((resolve, reject) => {
      this.fs.rename(oldPath, newPath, (err: NodeJS.ErrnoException | null) => {
        if (err !== null) {
          reject(err);
        } else {
          resolve();
        }
      })
    });
  }

  async mkdir(dirPath: string): Promise<void> {
    try {
      await new Promise<void>((resolve, reject) => {
        this.fs.mkdir(dirPath, undefined, (err: NodeJS.ErrnoException | null) => {
          if (err !== null) {
            reject(err);
          } else {
            resolve();
          }
        });
      });
    } catch (ex) {
      if (exceptionCode(ex) !== 'EEXIST') {
        throw ex;
      }
      return;
    }
    const fullDirectoryAccessForEveryone = 0b111111111;
    await ensureSensibleAccessPermissionsCallbackBased(this.fs, this.pt, dirPath, fullDirectoryAccessForEveryone);
  }

  async unlink(path: string): Promise<boolean> {
    try {
      await new Promise<void>((resolve, reject) => {
        this.fs.unlink(path, (err: NodeJS.ErrnoException | null) => {
          if (err !== null) {
            reject(err);
          } else {
            resolve();
          }
        });
      });
      return true;
    } catch (ex) {
      if (!isFileOrDirectoryMissingError(ex)) {
        throw ex;
      }
      return false;
    }
  }
  async rmdir(dirPath: string): Promise<void> {
    try {
      await new Promise<void>((resolve, reject) => {
        this.fs.rmdir(dirPath, {}, (err: NodeJS.ErrnoException | null) => {
          if (err !== null) {
            reject(err);
          } else {
            resolve();
          }
        });
      });
    } catch (ex) {
      if (!isFileOrDirectoryMissingError(ex)) {
        throw ex;
      }
    }
  }
}

async function promisifiedStat(fs: FsType, pt: string): Promise<limitedFsNamespace.Stats> {
  return await new Promise<limitedFsNamespace.Stats>((resolve, reject) => {
    fs.stat(pt, (err: NodeJS.ErrnoException | null, stats: limitedFsNamespace.Stats) => {
      if (err !== null) {
        reject(err);
      } else {
        resolve(stats);
      }
    });
  });
}

async function promisifiedChmod(fs: FsType, pt: string, mode: Mode): Promise<void> {
  return await new Promise<void>((resolve, reject) => {
    fs.chmod(pt, mode, (err: NodeJS.ErrnoException | null) => {
      if (err !== null) {
        reject(err);
      } else {
        resolve();
      }
    });
  });
}

async function promisifiedChown(fs: FsType, pt: string, uid: number, gid: number): Promise<void> {
  return await new Promise<void>((resolve, reject) => {
    fs.chown(pt, uid, gid, (err: NodeJS.ErrnoException | null) => {
      if (err !== null) {
        reject(err);
      } else {
        resolve();
      }
    });
  });
}

// TODO: Why is additionalModeBits not used?
async function ensureSensibleAccessPermissionsCallbackBased(fs: FsType, pt: PathType, path: string, additionalModeBits: number) {
  try {
    const stats = await promisifiedStat(fs, path);

    // eslint-disable-next-line no-bitwise
    const newMode = stats.mode | 0b110110110;
    if (newMode != stats.mode) {
      await promisifiedChmod(fs, path, newMode);
    }

    const dirStats = await promisifiedStat(fs, pt.dirname(path));
    if (stats.uid != dirStats.uid || stats.gid != dirStats.gid) {
      await promisifiedChown(fs, path, dirStats.uid, dirStats.gid);
    }
  } catch (ex) {
    console.warn('Could not ensure sensible mode, uid and gid for', path);
  }
}
